import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { Campaign, CampaignOperation, OperationsStatus } from '../models/types';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="page">
      <div class="top">
        <div>
          <p class="ux-caption">Your voice agents</p>
          <h1>Campaigns & agents</h1>
          <p>Manage outgoing campaigns and your incoming-call receptionist.</p>
        </div>
        <div class="top-actions">
          <button class="ghost" (click)="load()" [disabled]="loading()">
            {{ loading() ? 'Refreshing…' : 'Refresh' }}
          </button>
          <button *ngIf="isAdmin()" (click)="showCreate.set(true)">New campaign</button>
        </div>
      </div>

      <section class="system" *ngIf="operations() as ops">
        <span
          class="dot"
          [class.live]="ops.dialer.online && ops.dialer.mode === 'live'"
          [class.warn]="ops.dialer.online && ops.dialer.mode !== 'live'"
        ></span>
        <div>
          <strong>Calling service: {{ ops.dialer.mode }}</strong>
          <p>{{ ops.dialer.message }}</p>
        </div>
        <span class="updated">Updated {{ ops.refreshed_at | date: 'mediumTime' }}</span>
      </section>

      <div class="create" *ngIf="showCreate()">
        <h3>Create a voice agent</h3>
        <p>Choose its purpose, then add instructions and review before activation.</p>
        <label for="draft-name">Agent name</label>
        <input id="draft-name" [(ngModel)]="draftName" placeholder="Campaign name" />
        <label for="draft-type">Purpose</label
        ><select id="draft-type" [(ngModel)]="draftType">
          <option value="outbound">Call contacts — outbound campaign</option>
          <option value="inbound">Answer callers — inbound receptionist</option>
        </select>
        <button (click)="create()" [disabled]="creating || !draftName.trim()">
          {{ creating ? 'Creating…' : 'Create & continue setup' }}
        </button>
        <button class="ghost" (click)="showCreate.set(false)">Cancel</button>
        <p class="err" *ngIf="error()">{{ error() }}</p>
      </div>

      <p class="err" *ngIf="error()">{{ error() }}</p>
      <div class="ux-toolbar">
        <div class="ux-segments" aria-label="Agent type">
          <button (click)="filter = 'all'" [attr.aria-pressed]="filter === 'all'">All agents</button
          ><button (click)="filter = 'outbound'" [attr.aria-pressed]="filter === 'outbound'">
            Outgoing</button
          ><button (click)="filter = 'inbound'" [attr.aria-pressed]="filter === 'inbound'">
            Incoming
          </button>
        </div>
        <input
          aria-label="Search agents"
          [(ngModel)]="search"
          placeholder="Search agents by name…"
        />
      </div>
      <div class="list" *ngIf="filtered().length; else empty">
        <a class="campaign" *ngFor="let c of filtered()" [routerLink]="['/campaigns', c.id]">
          <div class="campaign-top">
            <div>
              <strong>{{ c.name }}</strong>
              <div class="meta">
                {{
                  c.campaign_type === 'inbound'
                    ? 'Incoming calls · receptionist'
                    : 'Outgoing calls · campaign'
                }}
                · {{ c.language }} · {{ c.gemini_voice }}
              </div>
            </div>
            <span class="badge" [attr.data-state]="operation(c.id)?.execution_state || c.status">
              {{ (operation(c.id)?.execution_state || c.status).replaceAll('_', ' ') }}
            </span>
          </div>
          <ng-container *ngIf="operation(c.id) as op">
            <div class="metrics">
              <span
                ><b>{{ op.running_calls }}</b> live</span
              >
              <span *ngIf="c.campaign_type === 'outbound'"
                ><b>{{ op.lead_counts['pending'] || 0 }}</b> waiting to call</span
              >
              <span *ngIf="c.campaign_type === 'outbound'"
                ><b>{{ op.lead_counts['completed'] || 0 }}</b> completed</span
              >
              <span
                ><b>{{ op.total_calls }}</b> calls</span
              >
              <span class="bad" *ngIf="op.stale_calls || op.failed_forms">
                <b>{{ op.stale_calls + op.failed_forms }}</b> need attention
              </span>
            </div>
            <div class="progress" *ngIf="c.campaign_type === 'outbound'">
              <span [style.width.%]="op.completed_percent"></span>
            </div>
            <div class="progress-label" *ngIf="c.campaign_type === 'outbound'">
              {{ op.completed_percent }}% finished · calling hours {{ op.window }} {{ op.timezone }}
            </div>
            <p *ngIf="c.campaign_type === 'inbound'">
              Answers calls routed to this campaign. No lead import required. Check routing in the
              campaign settings.
            </p>
            <div class="blocker" *ngIf="op.blockers[0]">{{ op.blockers[0] }}</div>
          </ng-container>
          <div class="open">Open campaign →</div>
        </a>
      </div>
      <ng-template #empty>
        <div class="empty">
          <strong>{{
            campaigns().length ? 'No matching agents' : 'Create your first agent'
          }}</strong>
          <p>
            For outgoing calls, configure a campaign and import leads. For incoming calls, configure
            a receptionist and assign its inbound route.
          </p>
        </div>
      </ng-template>
    </div>
  `,
  styles: [
    `
      .page {
        display: grid;
        gap: 16px;
      }
      .top {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: end;
      }
      .top-actions {
        display: flex;
        gap: 8px;
      }
      h1 {
        margin: 0;
        font-family: Georgia, serif;
      }
      p {
        margin: 4px 0 0;
        color: #64748b;
      }
      button {
        border: 0;
        background: #0f766e;
        color: white;
        border-radius: 10px;
        padding: 10px 14px;
        cursor: pointer;
      }
      .ghost {
        background: #e2e8f0;
        color: #0f172a;
      }
      .create,
      .system,
      .campaign,
      .empty {
        background: white;
        border-radius: 16px;
        padding: 16px;
        display: grid;
        gap: 10px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      .system {
        display: flex;
        align-items: center;
        gap: 12px;
      }
      .system p {
        margin: 2px 0 0;
      }
      .updated {
        margin-left: auto;
        color: #64748b;
        font-size: 12px;
      }
      .dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background: #dc2626;
        box-shadow: 0 0 0 4px #fee2e2;
      }
      .dot.live {
        background: #059669;
        box-shadow: 0 0 0 4px #d1fae5;
      }
      .dot.warn {
        background: #d97706;
        box-shadow: 0 0 0 4px #fef3c7;
      }
      .list {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 12px;
      }
      input,
      select {
        border: 1px solid #cbd5e1;
        border-radius: 10px;
        padding: 10px;
      }
      .campaign {
        text-decoration: none;
        color: inherit;
        display: grid;
        gap: 12px;
      }
      .campaign-top {
        display: flex;
        justify-content: space-between;
        gap: 8px;
      }
      .meta {
        color: #64748b;
        font-size: 13px;
      }
      .err {
        color: #b91c1c;
      }
      .badge {
        align-self: start;
        border-radius: 999px;
        padding: 4px 9px;
        background: #e2e8f0;
        color: #334155;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
      }
      .badge[data-state='running'],
      .badge[data-state='ready'] {
        background: #d1fae5;
        color: #047857;
      }
      .badge[data-state='active'],
      .badge[data-state='waiting_for_inbound'] {
        background: #dbeafe;
        color: #1d4ed8;
      }
      .badge[data-state='blocked'],
      .badge[data-state='attention'] {
        background: #fee2e2;
        color: #b91c1c;
      }
      .metrics {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 14px;
        color: #475569;
        font-size: 13px;
      }
      .bad {
        color: #b91c1c;
      }
      .progress {
        height: 8px;
        border-radius: 99px;
        background: #e2e8f0;
        overflow: hidden;
      }
      .progress span {
        display: block;
        height: 100%;
        background: #0f766e;
      }
      .progress-label {
        color: #64748b;
        font-size: 12px;
      }
      .blocker {
        color: #9a3412;
        background: #fff7ed;
        border-radius: 8px;
        padding: 8px;
        font-size: 13px;
      }
      .open {
        color: #0f766e;
        font-weight: 700;
        font-size: 13px;
      }
    `,
  ],
})
export class CampaignsPage implements OnInit, OnDestroy {
  campaigns = signal<Campaign[]>([]);
  operations = signal<OperationsStatus | null>(null);
  loading = signal(false);
  showCreate = signal(false);
  error = signal('');
  filter = 'all';
  search = '';
  filtered() {
    return this.campaigns().filter(
      (c) =>
        (this.filter === 'all' || c.campaign_type === this.filter) &&
        c.name.toLowerCase().includes(this.search.toLowerCase().trim()),
    );
  }
  draftName = '';
  creating = false;
  draftType: 'inbound' | 'outbound' = 'outbound';
  private refreshTimer?: ReturnType<typeof setInterval>;

  constructor(
    private readonly api: ApiService,
    private readonly auth: AuthService,
    private readonly router: Router,
  ) {}

  isAdmin(): boolean {
    return this.auth.user()?.role === 'admin';
  }

  async ngOnInit(): Promise<void> {
    await this.load();
    this.refreshTimer = setInterval(() => void this.load(true), 5000);
  }

  ngOnDestroy(): void {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
  }

  operation(id: string): CampaignOperation | undefined {
    return this.operations()?.campaigns.find((item) => item.campaign_id === id);
  }

  async load(silent = false): Promise<void> {
    if (this.loading()) return;
    if (!silent) this.loading.set(true);
    this.error.set('');
    try {
      const [campaigns, operations] = await Promise.all([
        this.api.listCampaigns(),
        this.api.operations(),
      ]);
      this.campaigns.set(campaigns);
      this.operations.set(operations);
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Could not load campaign status');
    } finally {
      this.loading.set(false);
    }
  }

  async create(): Promise<void> {
    if (this.creating || !this.draftName.trim()) return;
    this.creating = true;
    this.error.set('');
    try {
      const created = await this.api.createCampaign({
        name: this.draftName.trim(),
        campaign_type: this.draftType,
        language: 'ur-PK',
        gemini_voice: 'Aoede',
        script_text: '',
        flow_json: {
          nodes: [
            { id: 'start', type: 'Start', label: 'Start' },
            { id: 'greet', type: 'Greeting', label: 'Greet' },
            { id: 'script', type: 'SpeakScript', label: 'Script' },
            { id: 'rag', type: 'RagAnswer', label: 'Knowledge' },
            { id: 'end', type: 'TransferOrEnd', label: 'End' },
          ],
          edges: [
            { from: 'start', to: 'greet' },
            { from: 'greet', to: 'script' },
            { from: 'script', to: 'rag' },
            { from: 'rag', to: 'end' },
          ],
        },
        human_gate_enabled: true,
        human_gate_prompt: 'Hello?',
        max_call_minutes: 5,
        retry_after_minutes: 30,
        max_attempts: 5,
        window_start: '09:00',
        window_end: '22:00',
        timezone: 'Asia/Karachi',
      } as Partial<Campaign>);
      this.showCreate.set(false);
      this.draftName = '';
      this.campaigns.set([created, ...this.campaigns()]);
      await this.router.navigate(['/campaigns', created.id], { queryParams: { setup: '1' } });
    } catch (e: any) {
      this.error.set(e?.error?.detail || 'Create failed');
    } finally {
      this.creating = false;
    }
  }
}
